import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { detectSubdomain } from '../utils/tenantUtils';
import {
  LockClosedIcon,
  UserIcon,
  KeyIcon,
  ExclamationCircleIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subdomainContext, setSubdomainContext] = useState<{
    subdomain: string | null;
    isTenantLogin: boolean;
    isAdminPortal: boolean;
  } | null>(null);

  const { login, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the page the user was trying to access
  const from = (location.state as any)?.from?.pathname || '/';

  // Detect subdomain on mount
  useEffect(() => {
    const context = detectSubdomain();
    setSubdomainContext(context);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
      // Redirect to the page they were trying to access, or home
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg">
            {subdomainContext?.isTenantLogin ? (
              <BuildingOffice2Icon className="h-12 w-12 text-white" />
            ) : (
              <LockClosedIcon className="h-12 w-12 text-white" />
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {tenant?.tenantName ||
              (subdomainContext?.isTenantLogin
                ? `${subdomainContext.subdomain} Portal`
                : 'MDM Solution')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {subdomainContext?.isTenantLogin ? 'Tenant Login' : 'Admin Portal'}
          </p>
          {subdomainContext?.isTenantLogin && (
            <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <BuildingOffice2Icon className="w-4 h-4 mr-1" />
              {subdomainContext.subdomain}
            </div>
          )}
        </div>

        {/* Login Form */}
        <div className="mt-8 bg-white py-8 px-6 shadow-2xl rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Alert */}
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* Username Input */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          {/* Development Mode Info - SECURITY: No hardcoded credentials */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Development Mode</span>
                </div>
              </div>

              <div className="mt-4 rounded-md bg-blue-50 p-4">
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-2">
                    {subdomainContext?.isTenantLogin
                      ? `${subdomainContext.subdomain?.toUpperCase()} Tenant`
                      : 'Multi-Tenant Access'}
                  </p>

                  {/* Tenant-specific info without credentials */}
                  {subdomainContext?.isTenantLogin && (
                    <div className="space-y-1">
                      <p className="text-blue-900">
                        You are accessing the <strong>{subdomainContext.subdomain}</strong> tenant.
                      </p>
                      <p className="text-xs text-blue-600 mt-2">
                        Contact your administrator for login credentials.
                      </p>
                    </div>
                  )}

                  {/* Default/No Subdomain */}
                  {!subdomainContext?.isTenantLogin && (
                    <div className="space-y-1">
                      <p className="text-blue-900">Use subdomain URLs for tenant access:</p>
                      <p className="mt-2 text-xs text-blue-600">
                        Example: tenant-name.yourdomain.com
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-gray-500">
          &copy; 2025 MDM Solution. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
